import { useMemo } from 'react';
import { queryOptions, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  appModule,
  generalPreferences,
  preferencesResponse,
  type AppModule,
  type GeneralPreferences,
  type PreferencesResponse,
  type ScreenPreferences,
} from '@overseer/contracts';
import { request } from '@/lib/api/client';
import { currentLocale, setLocale, type Locale } from '@/lib/i18n';
import { setThemeChoice, type ThemeChoice } from '@/lib/theme';
import { clearDefaultModule, readDefaultModule, writeDefaultModule } from '@/lib/default-module';

/**
 * What a person chose for themselves, kept on the server so it follows them to
 * another browser.
 *
 * localStorage still holds the language, the theme and the default module: it
 * is what the first paint reads, before any request can answer. The server's
 * answer then wins, once per sign-in. A choice made here writes both.
 *
 * Every write is fire-and-forget. A preference that did not save is logged; it
 * never blocks the screen or raises an error the person has to dismiss.
 */

export const PREFERENCES_KEY = ['preferences'] as const;

/**
 * `GET /v1/me/preferences`.
 *
 * Never stale: this tab writes every change into the cache as it sends it, so a
 * refetch could only bring back an answer older than what the cache holds.
 */
export const preferencesQuery = () =>
  queryOptions({
    queryKey: PREFERENCES_KEY,
    queryFn: ({ signal }) => request('/v1/me/preferences', preferencesResponse, { signal }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

const logFailure = (what: string) => (error: unknown) => {
  console.error(`Could not save ${what}`, error);
};

const updateCache = (
  client: QueryClient,
  change: (current: PreferencesResponse) => PreferencesResponse,
): void => {
  client.setQueryData<PreferencesResponse>(PREFERENCES_KEY, (current) =>
    current ? change(current) : current,
  );
};

/**
 * The general fields the person changed in this sign-in.
 *
 * The server's answer must not undo a choice made while it was in flight: a
 * language picked in the second the request takes is the newer choice.
 */
const chosenThisSession = new Set<keyof GeneralPreferences>();

/** Forget the session's choices. On sign-out, so the next person starts clean. */
export const resetPreferenceSession = (): void => {
  chosenThisSession.clear();
};

/** `PUT /v1/me/preferences/general` with only the fields that changed. */
export const saveGeneral = (client: QueryClient, patch: GeneralPreferences): Promise<void> => {
  for (const key of Object.keys(patch) as (keyof GeneralPreferences)[]) {
    chosenThisSession.add(key);
  }

  updateCache(client, (current) => ({ ...current, general: { ...current.general, ...patch } }));

  return request('/v1/me/preferences/general', generalPreferences, { method: 'PUT', body: patch })
    .then(() => undefined)
    .catch(logFailure('a preference'));
};

/**
 * Applies the server's general preferences in this browser.
 *
 * Only what the server holds: an absent field leaves this browser's own value.
 * A `defaultModule` of `null` is a stored "no default", so it clears the local one.
 * Fields the person changed since signing in are left alone.
 */
export const applyGeneral = (general: GeneralPreferences): void => {
  if (general.locale && !chosenThisSession.has('locale') && general.locale !== currentLocale()) {
    void setLocale(general.locale);
  }

  if (general.theme && !chosenThisSession.has('theme')) {
    setThemeChoice(general.theme);
  }

  if (general.defaultModule !== undefined && !chosenThisSession.has('defaultModule')) {
    if (general.defaultModule === null) {
      clearDefaultModule();
    } else {
      writeDefaultModule(general.defaultModule);
    }
  }
};

/**
 * The module to open after sign-in: the server's choice, then this browser's.
 * A module the person no longer has is no default at all.
 */
export const defaultModuleFor = (
  available: AppModule[],
  general: GeneralPreferences | undefined,
): AppModule | null => {
  const saved = appModule.safeParse(general?.defaultModule);

  if (saved.success && available.includes(saved.data)) {
    return saved.data;
  }

  return general?.defaultModule === null ? null : readDefaultModule(available);
};

/**
 * Change a general preference: here at once, and on the server for next time.
 */
export const useChoosePreference = () => {
  const client = useQueryClient();

  return useMemo(
    () => ({
      locale: (locale: Locale) => {
        void setLocale(locale);
        void saveGeneral(client, { locale });
      },
      theme: (theme: ThemeChoice) => {
        setThemeChoice(theme);
        void saveGeneral(client, { theme });
      },
      defaultModule: (module: AppModule) => {
        writeDefaultModule(module);
        void saveGeneral(client, { defaultModule: module });
      },
    }),
    [client],
  );
};

const screenPath = (screen: string) => `/v1/me/preferences/screens/${encodeURIComponent(screen)}`;

/**
 * Save one screen's filters and sorting. An empty object removes the entry: a
 * screen left on its defaults has nothing to remember.
 */
export const saveScreen = (
  client: QueryClient,
  screen: string,
  value: ScreenPreferences,
): Promise<void> => {
  const empty = Object.keys(value).length === 0;

  updateCache(client, (current) => {
    const screens = { ...current.screens };

    if (empty) {
      delete screens[screen];
    } else {
      screens[screen] = value;
    }

    return { ...current, screens };
  });

  return request(
    screenPath(screen),
    z.void(),
    empty ? { method: 'DELETE' } : { method: 'PUT', body: value },
  ).catch(logFailure(`the ${screen} filters`));
};

/**
 * Forget every screen's saved filters and sorting.
 *
 * Asks the server which screens it holds rather than trusting the cache, so a
 * screen saved from another browser goes too. Rejects when a request fails: the
 * person asked for this and must hear if it did not happen.
 */
export const forgetScreens = async (client: QueryClient): Promise<void> => {
  const stored = await request('/v1/me/preferences', preferencesResponse);
  const screens = Object.keys(stored.screens);

  await Promise.all(
    screens.map((screen) => request(screenPath(screen), z.void(), { method: 'DELETE' })),
  );

  client.setQueryData<PreferencesResponse>(PREFERENCES_KEY, { ...stored, screens: {} });
};
